import { API } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import { MemberInterface } from "@swivl-mono/types";
import { SearchInput, Spinner } from "@swivl-mono/ui";
import { useEffect, useMemo, useRef, useState } from "react";

import { LocalStorageHelper } from "../../../Models/LocalStorage/LocalStorageHelper";

const FALLBACK_AVATAR = "https://swivl-cdn.s3-us-west-1.amazonaws.com/Hoover-teams-192.png";
const AVATAR_CACHE_KEY = "orgAvatars";
const AVATAR_CACHE_TTL = 86400000; // 24 hours

interface AvatarsState {
  loaded: boolean;
  avatars: { [orgId: number]: string };
}

interface OrgPickerProps {
  memberships: MemberInterface[];
  currentOrgId: number;
  isOpen: boolean;
  onSelect: (membership: MemberInterface) => void;
}

export function OrgPicker({ memberships, currentOrgId, isOpen, onSelect }: OrgPickerProps): React.ReactNode {
  const [searchQuery, setSearchQuery] = useState("");
  const [avatarsState, setAvatarsState] = useState<AvatarsState>({ loaded: false, avatars: {} });
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  // A membership can reference a soft-deleted org, in which case the API returns
  // `org: null`. Drop those up front so the picker never dereferences a null org.
  const validMemberships = useMemo(() => memberships.filter((m) => m.org != null), [memberships]);

  // Load avatars on first open (with localStorage cache)
  useEffect(() => {
    if (!isOpen || avatarsState.loaded || validMemberships.length === 0) return;

    const cached = LocalStorageHelper.getItem(AVATAR_CACHE_KEY, true) as { avatars: { [orgId: number]: string }; timestamp: number } | null;

    if (cached && Date.now() - cached.timestamp < AVATAR_CACHE_TTL) {
      setAvatarsState({ loaded: true, avatars: cached.avatars });
      return;
    }

    const orgIds = validMemberships.map((m) => m.org.id).join(",");
    API.Org.avatarsForOrgs(orgIds)
      .then((avatars) => {
        LocalStorageHelper.setItem(AVATAR_CACHE_KEY, { avatars, timestamp: Date.now() });
        setAvatarsState({ loaded: true, avatars });
      })
      .catch(() => {
        setAvatarsState({ loaded: true, avatars: {} });
      });
  }, [isOpen, avatarsState.loaded, validMemberships]);

  // Reset search when closed
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
    }
  }, [isOpen]);

  const sortedMemberships = useMemo(() => [...validMemberships].sort((a, b) => a.org.name.localeCompare(b.org.name)), [validMemberships]);

  const filteredMemberships = useMemo(() => {
    if (!searchQuery) return sortedMemberships;
    const query = searchQuery.toLowerCase();
    return sortedMemberships.filter((m) => m.org.name.toLowerCase().includes(query));
  }, [sortedMemberships, searchQuery]);

  // Reset highlight when filtered list changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredMemberships]);

  // Scroll highlighted item into view
  useEffect(() => {
    const container = listRef.current;
    if (!container) return;
    const child = container.children[highlightedIndex] as HTMLElement | undefined;
    child?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, filteredMemberships.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredMemberships[highlightedIndex]) {
        onSelect(filteredMemberships[highlightedIndex]);
      }
    }
  };

  const getAvatarUrl = (orgId: number): string => {
    return avatarsState.avatars[orgId] || FALLBACK_AVATAR;
  };

  return (
    <div className="flex flex-col gap-2" onKeyDown={handleKeyDown}>
      <SearchInput
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
        value={searchQuery}
        onClear={() => setSearchQuery("")}
        name="org-search"
        autoFocus
        placeholder="Search orgs..."
        containerClassName="w-full"
      />

      <div ref={listRef} className="max-h-[300px] overflow-y-auto">
        {filteredMemberships.length === 0 && <div className="px-2 py-3 text-center text-sm text-grey-500">No orgs found</div>}

        {filteredMemberships.map((membership, index) => {
          const isActive = membership.org.id === currentOrgId;
          const isHighlighted = index === highlightedIndex;

          return (
            <button
              key={membership.id}
              className={filterClasses(
                "flex w-full items-center gap-2 rounded px-2 py-2 text-left transition-colors",
                isActive ? "bg-orange-50 text-orange-500" : "text-grey-900 hover:bg-grey-100",
                isHighlighted && !isActive ? "bg-grey-100" : ""
              )}
              onClick={() => onSelect(membership)}
            >
              <div className="h-7 w-7 flex-shrink-0 overflow-hidden rounded">
                {avatarsState.loaded ? (
                  <img src={getAvatarUrl(membership.org.id)} alt={membership.org.name} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <Spinner />
                  </div>
                )}
              </div>
              <span className="truncate text-sm font-medium">{membership.org.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
